import { motion } from "framer-motion";
import { Compass } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_IMAGES } from "../../data/holidays";
import { Chapter, Reveal, EASE } from "./shared";

export default function HolidayMore() {
  const { t } = useTranslation();
  const M = HOLIDAY_IMAGES.more;
  const alt = (k) => t(`holidays.more.alts.${k}`);
  return (
    <section className="bg-forest-deep text-bone py-24 lg:py-40 relative overflow-hidden" data-testid="holidays-more">
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <Chapter number={t("holidays.more.chapter")} label={t("holidays.more.eyebrow")} tone="dark" />
        <Reveal delay={0.1}>
          <h2 className="font-display leading-[0.9] mt-8 uppercase tracking-tight text-[15vw] sm:text-8xl lg:text-[9rem]">
            More <span className="italic normal-case">than</span><br />riding
          </h2>
        </Reveal>

        <div className="mt-16 lg:mt-24 grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-8">
          <Card
            index="A"
            title={t("holidays.more.cards.hut.title")}
            text={t("holidays.more.cards.hut.text")}
            note={t("holidays.more.cards.hut.note")}
            images={[{ src: M.hutView, alt: alt("hutView") }, { src: M.hutLangosi, alt: alt("hutLangosi") }, { src: M.hutMeal, alt: alt("hutMeal") }]}
            testid="more-card-hut"
          />
          <Card
            index="B"
            title={t("holidays.more.cards.evening.title")}
            text={t("holidays.more.cards.evening.text")}
            note={t("holidays.more.cards.evening.note")}
            images={[{ src: M.terrace, alt: alt("terrace") }, { src: M.hutBread, alt: alt("hutBread") }, { src: M.hutPolenta, alt: alt("hutPolenta") }]}
            testid="more-card-evening"
            delay={0.15}
          />
        </div>

        <Reveal delay={0.2} className="mt-12 lg:mt-16 flex items-start gap-4 border-t border-bone/15 pt-8 max-w-2xl">
          <Compass className="w-5 h-5 text-terracotta shrink-0 mt-0.5 stroke-[1.5]" />
          <p className="text-base md:text-lg text-bone/80 leading-relaxed" data-testid="more-free-time">{t("holidays.more.free_time")}</p>
        </Reveal>
      </div>
    </section>
  );
}

function Card({ index, title, text, note, images, testid, delay = 0 }) {
  return (
    <motion.article
      data-testid={testid}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -60px 0px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className="group bg-forest border border-bone/10 flex flex-col"
    >
      <div className="grid grid-cols-3 gap-px bg-bone/10">
        {images.map((im, i) => (
          <div key={i} className={`relative overflow-hidden bg-forest-deep ${i === 0 ? "col-span-2 aspect-[4/3]" : "aspect-[2/3]"}`}>
            <img src={im.src} alt={im.alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover cine-img" />
          </div>
        ))}
      </div>
      <div className="p-7 lg:p-10 flex-1 flex flex-col">
        <span className="font-display text-2xl text-bone/40 leading-none">{index}</span>
        <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mt-5 uppercase">{title}</h3>
        <p className="mt-6 text-base md:text-lg text-bone/85 leading-relaxed">{text}</p>
        <p className="mt-6 text-[10px] uppercase tracking-wider-3 text-terracotta">{note}</p>
      </div>
    </motion.article>
  );
}
